<script setup lang="ts">
import type { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper } from '@tiptap/vue-3';
import { ExternalLink, Trash2 } from 'lucide-vue-next';
import { computed, inject, ref } from 'vue';
import type { Ref } from 'vue';
import type { ReferenceItem } from '@/components/references/ReferenceListItem.vue';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { formatInline } from '@/lib/citations/formatters';
import type { CitationStyle } from '@/lib/citations/formatters';

const props = defineProps<NodeViewProps>();

const open = ref(false);

// References, citation order, and style are provided by EditorCanvas via provide/inject.
const injectedRefs = inject<Ref<ReferenceItem[]>>('editorReferences');
const _citedOrderRef = inject<Ref<string[]>>('editorCitedOrder');
const citationStyle = inject<string>('editorCitationStyle', 'apa');

const reference = computed<ReferenceItem | undefined>(() =>
    (injectedRefs?.value ?? []).find((r) => r.id === props.node.attrs.paperId),
);

/**
 * Compute the 1-based order-of-appearance index for this citation.
 * Used by Vancouver ([1], [2], …).
 */
const appearanceIndex = computed<number>(() => {
    const order = _citedOrderRef?.value ?? [];
    const idx = order.indexOf(props.node.attrs.paperId);

    return idx >= 0 ? idx + 1 : 1;
});

/**
 * Dynamic label based on the active citation style.
 * Falls back to the stored `label` attr for legacy documents.
 */
const displayLabel = computed<string>(() => {
    if (!reference.value) {
        return props.node.attrs.label;
    }

    return formatInline(
        reference.value,
        (citationStyle as CitationStyle) ?? 'apa',
        appearanceIndex.value,
    );
});

const authorsDisplay = computed(() => {
    const ref = reference.value;

    if (!ref) {
        return '';
    }

    const authors = Array.isArray(ref.authors) ? ref.authors : [ref.authors];

    return (
        authors.slice(0, 3).join(', ') + (authors.length > 3 ? ' et al.' : '')
    );
});
</script>

<template>
    <NodeViewWrapper as="span" class="inline">
        <Popover v-model:open="open">
            <PopoverTrigger as-child>
                <span
                    class="citation cursor-pointer rounded px-0.5 font-medium text-brand underline decoration-dotted underline-offset-2 hover:bg-brand/10"
                    :data-paper-id="props.node.attrs.paperId"
                    :data-paper-key="props.node.attrs.paperKey"
                    contenteditable="false"
                    >{{ displayLabel }}</span
                >
            </PopoverTrigger>

            <PopoverContent class="w-72 space-y-2 p-3 text-sm" @click.stop>
                <div v-if="reference">
                    <p class="line-clamp-3 leading-snug font-medium">
                        {{ reference.title }}
                    </p>
                    <p class="mt-1 text-xs text-muted-foreground">
                        {{ authorsDisplay
                        }}<span v-if="reference.year">
                            · {{ reference.year }}</span
                        >
                    </p>
                    <a
                        v-if="reference.doi"
                        :href="`https://doi.org/${reference.doi}`"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="mt-1 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                    >
                        <ExternalLink class="h-3 w-3" />
                        DOI
                    </a>
                </div>
                <p v-else class="text-xs text-muted-foreground italic">
                    Référence introuvable (id: {{ props.node.attrs.paperId }})
                </p>

                <div class="flex justify-end border-t border-border/40 pt-2">
                    <button
                        class="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                        @click="
                            props.deleteNode();
                            open = false;
                        "
                    >
                        <Trash2 class="h-3 w-3" />
                        Retirer
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    </NodeViewWrapper>
</template>
